<template>
  <div
    class="selection-list-frame"
    style="height: max-content"
    :data-visible-rows="resolvedVisibleRows"
  >
    <div class="selection-list" :role="listRole || undefined">
      <div v-if="items.length === 0" class="selection-empty">{{ emptyText }}</div>
      <div
        v-for="(item, index) in items"
        v-else
        :key="resolveItemKey(item, index)"
        class="selection-item"
        :class="{
          active: resolvedActiveIndex === index,
          selected: isItemSelected(index),
          disabled: disabled,
        }"
        @click="handleItemClick($event, item, index)"
        @contextmenu.prevent="handleItemContextMenu($event, item, index)"
      >
        <slot
          name="item"
          :item="item"
          :index="index"
          :active="resolvedActiveIndex === index"
          :selected="isItemSelected(index)"
        >
          {{ resolveItemLabel(item) }}
        </slot>
      </div>
    </div>

    <n-dropdown
      v-if="contextMenuEnabled"
      placement="bottom-start"
      trigger="manual"
      :x="contextMenuX"
      :y="contextMenuY"
      :options="contextMenuOptions"
      :show="showContextMenu"
      @select="handleContextMenuSelect"
      @clickoutside="showContextMenu = false"
    />
  </div>
</template>

<script setup lang="ts" generic="TItem = unknown">
import { computed, ref, watch } from 'vue';
import { NDropdown, type DropdownOption } from 'naive-ui';

const SELECTION_ITEM_MIN_HEIGHT = 24;
const SELECTION_ITEM_GAP = 4;
const FRAME_VERTICAL_PADDING = 12;
const DEFAULT_VISIBLE_ROWS = 2;

enum SelectionListContextAction {
  DeleteAll = 'delete-all',
  Delete = 'delete',
}

/**
 * @description 选择行为模式枚举。
 */
enum SelectionBehaviorMode {
  /**
   * @description 单选模式，仅保留最后一次点击项。
   */
  Single = 'single',
  /**
   * @description Ctrl/Meta 多选模式，普通点击单选，Ctrl/Meta 点击切换多选项。
   */
  CtrlMultiple = 'ctrl-multiple',
}

interface SelectionListFrameProps {
  items: readonly TItem[];
  activeIndex?: number;
  selectedIndices?: readonly number[];
  selectionBehavior?: 'single' | 'ctrl-multiple';
  visibleRows?: number;
  listRole?: string;
  emptyText?: string;
  itemKeyField?: string;
  itemLabelField?: string;
  getItemKey?: (item: unknown, index: number) => string | number;
  isItemDeleteEnabled?: (item: unknown, index: number) => boolean;
  contextMenuEnabled?: boolean;
  disabled?: boolean;
  deleteLabel?: string;
  deleteAllLabel?: string;
  showDeleteAction?: boolean;
  showDeleteAllAction?: boolean;
}

const props = withDefaults(defineProps<SelectionListFrameProps>(), {
  showDeleteAction: true,
  showDeleteAllAction: true,
});
const emit = defineEmits<{
  (event: 'update:activeIndex', value: number): void;
  (event: 'update:selectedIndices', value: number[]): void;
  (event: 'item-click', payload: {
    item: unknown;
    index: number;
    selectedIndices: number[];
    isModifierPressed: boolean;
  }): void;
  (event: 'delete-item', payload: { item: unknown; index: number }): void;
  (event: 'delete-all'): void;
}>();

//------ SelectionListFrame-列表点击与Ctrl多选 开始------
const showContextMenu = ref(false);
const contextMenuX = ref(0);
const contextMenuY = ref(0);
const contextMenuTarget = ref<{ item: unknown; index: number } | null>(null);
const internalActiveIndex = ref(-1);
const internalSelectedIndices = ref<number[]>([]);

const resolvedVisibleRows = computed(() => {
  const rows = Number(props.visibleRows ?? DEFAULT_VISIBLE_ROWS);
  if (!Number.isFinite(rows) || rows <= 0) {
    return DEFAULT_VISIBLE_ROWS;
  }
  return Math.max(1, Math.round(rows));
});

const frameHeight = computed(() => `${(
  SELECTION_ITEM_MIN_HEIGHT * resolvedVisibleRows.value
  + SELECTION_ITEM_GAP * (resolvedVisibleRows.value - 1)
  + FRAME_VERTICAL_PADDING
)}px`);

const frameStyle = computed(() => ({
  // minHeight: frameHeight.value,
  // maxHeight: frameHeight.value,
  height: frameHeight.value,
}));

const resolvedSelectionBehavior = computed<SelectionBehaviorMode>(() => {
  if (props.selectionBehavior === SelectionBehaviorMode.CtrlMultiple) {
    return SelectionBehaviorMode.CtrlMultiple;
  }
  return SelectionBehaviorMode.Single;
});

const resolvedActiveIndex = computed<number>(() => (
  typeof props.activeIndex === 'number' ? props.activeIndex : internalActiveIndex.value
));

const resolvedSelectedIndices = computed<number[]>(() => {
  const source = Array.isArray(props.selectedIndices)
    ? props.selectedIndices
    : internalSelectedIndices.value;
  return normalizeSelectedIndices(source);
});

const resolvedItemLabelField = computed(() => props.itemLabelField ?? 'label');


const contextMenuOptions = computed<DropdownOption[]>(() => {
  const deleteDisabled = contextMenuTarget.value
    ? !resolveItemDeleteEnabled(contextMenuTarget.value.item, contextMenuTarget.value.index)
    : false;
  const options: DropdownOption[] = [];

  if (props.showDeleteAllAction ?? true) {
    options.push({
      label: props.deleteAllLabel ?? '删除所有',
      key: SelectionListContextAction.DeleteAll,
    });
  }

  if (props.showDeleteAction ?? true) {
    options.push({
      label: props.deleteLabel ?? '删除',
      key: SelectionListContextAction.Delete,
      disabled: deleteDisabled,
    });
  }

  return options;
});

function resolveItemKey(item: unknown, index: number): string | number {
  if (props.getItemKey) {
    return props.getItemKey(item, index);
  }
  if (props.itemKeyField && item && typeof item === 'object') {
    const key = (item as Record<string, unknown>)[props.itemKeyField];
    if (typeof key === 'string' || typeof key === 'number') {
      return key;
    }
  }
  return index;
}

function resolveItemLabel(item: unknown): string {
  if (!item || typeof item !== 'object') {
    return '';
  }
  const value = (item as Record<string, unknown>)[resolvedItemLabelField.value];
  return typeof value === 'string' ? value : '';
}

/**
 * @description 判断当前列表项是否允许执行删除动作。
 * @param item 列表项。
 * @param index 列表索引。
 * @returns 是否允许删除。
 */
function resolveItemDeleteEnabled(item: unknown, index: number): boolean {
  return props.isItemDeleteEnabled?.(item, index) ?? true;
}

/**
 * @description 归一化选中索引，保证索引唯一、有效并按升序输出。
 * @param indices 原始索引列表。
 * @returns 归一化后的索引列表。
 */
function normalizeSelectedIndices(indices: readonly number[]): number[] {
  const maxIndex = props.items.length - 1;
  const uniqueSorted = Array.from(new Set(indices))
    .filter((index) => Number.isInteger(index) && index >= 0 && index <= maxIndex)
    .sort((left, right) => left - right);
  return uniqueSorted;
}

/**
 * @description 判断指定索引是否为当前选中项。
 * @param index 列表索引。
 * @returns 是否选中。
 */
function isItemSelected(index: number): boolean {
  return resolvedSelectedIndices.value.includes(index);
}

/**
 * @description 更新激活索引（支持受控与非受控两种模式）。
 * @param index 目标索引。
 */
function updateActiveIndex(index: number): void {
  if (typeof props.activeIndex !== 'number') {
    internalActiveIndex.value = index;
  }
  emit('update:activeIndex', index);
}

/**
 * @description 更新选中索引集合（支持受控与非受控两种模式）。
 * @param indices 目标索引集合。
 */
function updateSelectedIndices(indices: number[]): void {
  const normalized = normalizeSelectedIndices(indices);
  if (!Array.isArray(props.selectedIndices)) {
    internalSelectedIndices.value = normalized;
  }
  emit('update:selectedIndices', normalized);
}

/**
 * @description 根据交互行为计算新的选中索引集合。
 * @param index 当前点击索引。
 * @param isModifierPressed 是否按下 Ctrl/Meta 修饰键。
 * @returns 新的选中索引集合。
 */
function resolveNextSelectedIndices(index: number, isModifierPressed: boolean): number[] {
  if (resolvedSelectionBehavior.value !== SelectionBehaviorMode.CtrlMultiple || !isModifierPressed) {
    return [index];
  }
  if (isItemSelected(index)) {
    return resolvedSelectedIndices.value.filter((selectedIndex) => selectedIndex !== index);
  }
  return [...resolvedSelectedIndices.value, index];
}

/**
 * @description 处理条目点击，封装单选与 Ctrl/Meta 多选行为。
 * @param event 鼠标事件。
 * @param item 当前条目。
 * @param index 当前索引。
 */
function handleItemClick(event: MouseEvent, item: unknown, index: number): void {
  if (props.disabled) {
    return;
  }
  const isModifierPressed = Boolean(event.ctrlKey || event.metaKey);
  const nextSelectedIndices = resolveNextSelectedIndices(index, isModifierPressed);
  updateSelectedIndices(nextSelectedIndices);
  updateActiveIndex(index);
  emit('item-click', {
    item,
    index,
    selectedIndices: normalizeSelectedIndices(nextSelectedIndices),
    isModifierPressed,
  });
}

function handleItemContextMenu(event: MouseEvent, item: unknown, index: number): void {
  if (props.disabled) {
    return;
  }
  updateActiveIndex(index);
  if (!isItemSelected(index)) {
    updateSelectedIndices([index]);
  }
  if (!props.contextMenuEnabled) {
    return;
  }
  contextMenuTarget.value = { item, index };
  contextMenuX.value = Math.floor(event.clientX);
  contextMenuY.value = Math.floor(event.clientY);
  showContextMenu.value = true;
}

function handleContextMenuSelect(key: string): void {
  showContextMenu.value = false;

  if (key === SelectionListContextAction.DeleteAll) {
    emit('delete-all');
    contextMenuTarget.value = null;
    return;
  }

  if (key === SelectionListContextAction.Delete && contextMenuTarget.value) {
    if (!resolveItemDeleteEnabled(contextMenuTarget.value.item, contextMenuTarget.value.index)) {
      contextMenuTarget.value = null;
      return;
    }
    emit('delete-item', contextMenuTarget.value);
    contextMenuTarget.value = null;
  }
}

/**
 * @description 列表数据变化时，清理越界的激活索引与选中索引。
 */
watch(
  () => props.items.length,
  () => {
    if (resolvedActiveIndex.value >= props.items.length) {
      updateActiveIndex(-1);
    }
    if (resolvedSelectedIndices.value.length > 0) {
      updateSelectedIndices(resolvedSelectedIndices.value);
    }
  }
);
//------ SelectionListFrame-列表点击与Ctrl多选 结束------
</script>

<style scoped>
.selection-list {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.selection-list-frame {
  border: 1px solid var(--n-border-color, color-mix(in srgb, var(--n-text-color, #1f2937) 24%, transparent));
  border-radius: 4px;
  padding: 6px;
  font-size: 12px;
  overflow-y: auto;
}

.selection-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  min-height: 24px;
  border: 1px solid var(--n-border-color, color-mix(in srgb, var(--n-text-color, #1f2937) 24%, transparent));
  border-radius: 3px;
  padding: 2px 6px;
  color: var(--n-text-color);
}

.selection-item.active {
  border-color: var(--n-primary-color, var(--puppy-primary));
  background: color-mix(in srgb, var(--n-primary-color, var(--puppy-primary)) 12%, transparent);
}

.selection-item.selected:not(.active) {
  border-color: color-mix(in srgb, var(--n-primary-color, var(--puppy-primary)) 70%, transparent);
  background: color-mix(in srgb, var(--n-primary-color, var(--puppy-primary)) 8%, transparent);
}

.selection-item.disabled {
  opacity: 0.6;
  pointer-events: none;
}

.selection-empty {
  min-height: 24px;
  display: flex;
  align-items: center;
  padding: 2px 6px;
  color: var(--n-text-color-3);
}
</style>
